import { useMemo, useState, Fragment } from "react";
import { ChevronRight, ChevronDown, Search } from "lucide-react";
import { C } from "../constants/colors";
import { ACCELERATOR_MODULE_LABELS } from "../constants/modules";
import { timeAgo, accessStatus } from "../utils/admin";

/* Admin → Users as a TABLE (her ask, 2026-10-08): "make the users collapsible
   so it's easier to see… filter by name and user type and credits used".
   One row per person, sortable by any column heading; filters for name /
   email, usage tier and access; a row opens to what the old card showed —
   modules started, archetype, and the actions (Give credits, the trial
   buttons, See their usage). Rows stay closed by default so the list scans.

   Pure display: every action is handed in, so the network calls stay in
   AdminPanel where they were. */

const TIER_ORDER = { free: 0, beta: 1, paid: 2 };
const PHASE_ORDER = { ideation: 1, accelerator: 2, momentum: 3 };
const fmtTok = (v) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(v || 0));

const COLUMNS = [
  { key: "name", label: "Name", value: (u) => (u.name || u.email || "").toLowerCase() },
  { key: "tier", label: "Tier", value: (u) => TIER_ORDER[u.usageTier] ?? -1 },
  { key: "access", label: "Access", value: (u) => PHASE_ORDER[u.phase] ?? 0, wide: true },
  { key: "left", label: "Credits left", value: (u) => (u.credits ? u.credits.remaining : -Infinity), num: true },
  { key: "used", label: "Credits used", value: (u) => (u.credits ? u.credits.spent : -Infinity), num: true, wide: true },
  { key: "cost", label: "Cost", value: (u) => u.totalCost || 0, num: true, wide: true },
  { key: "active", label: "Last active", value: (u) => (u.lastActive ? new Date(u.lastActive).getTime() : 0), num: true },
];

const pill = (bg, color) => ({
  display: "inline-block", background: bg, color, borderRadius: 20, padding: "2px 9px", fontSize: 10, fontWeight: 600,
});
const TIER_PILL = {
  free: pill("#f0eff6", "#666"),
  beta: pill(`${C.amber}22`, "#8a6100"),
  paid: pill(`${C.teal}18`, C.teal),
};

export function AdminUsersTable({ users, creditsOn, isMobile, grantingId, onGiveCredits, onGrantAccess, onOpenUsage }) {
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");
  const [access, setAccess] = useState("all");
  const [sort, setSort] = useState({ key: "active", dir: "desc" });
  const [open, setOpen] = useState(() => new Set());

  /* On a phone the table keeps Name, Tier, Credits left and Last active; the
     rest is in the opened row. */
  const cols = COLUMNS.filter((c) => !isMobile || !c.wide);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const col = COLUMNS.find((c) => c.key === sort.key) || COLUMNS[0];
    return users
      .filter((u) => !needle || `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(needle))
      .filter((u) => tier === "all" || u.usageTier === tier)
      .filter((u) => access === "all" || (access === "none" ? !u.phase : u.phase === access))
      .slice()
      .sort((a, b) => {
        const x = col.value(a), y = col.value(b);
        const c = x < y ? -1 : x > y ? 1 : 0;
        return sort.dir === "asc" ? c : -c;
      });
  }, [users, q, tier, access, sort]);

  const toggle = (id) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const sortBy = (key) => setSort((s) => (s.key === key
    ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
    : { key, dir: key === "name" || key === "tier" ? "asc" : "desc" }));

  const select = { fontSize: 12, padding: "6px 8px", borderRadius: 8, border: "1px solid #e0dfe8", background: "#fff", color: "#444" };
  const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "#888", padding: "8px 10px", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", borderBottom: "1px solid #ebebf2" };
  const td = { padding: "10px", fontSize: 12, color: C.indigo, borderBottom: "1px solid #f2f1f6", verticalAlign: "middle" };

  return (
    <div>
      {/* Filters */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <div style={{ position: "relative", flex: "1 1 200px", minWidth: 0 }}>
          <Search size={13} color="#aaa" style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email"
            aria-label="Search users"
            style={{ ...select, width: "100%", boxSizing: "border-box", paddingLeft: 28 }} />
        </div>
        <select value={tier} onChange={(e) => setTier(e.target.value)} aria-label="Filter by usage tier" style={select}>
          <option value="all">All tiers</option>
          <option value="free">Free</option>
          <option value="beta">Beta</option>
          <option value="paid">Paid</option>
        </select>
        <select value={access} onChange={(e) => setAccess(e.target.value)} aria-label="Filter by access" style={select}>
          <option value="all">All access</option>
          <option value="ideation">Ideation</option>
          <option value="accelerator">Accelerator</option>
          <option value="momentum">Momentum</option>
          <option value="none">No access</option>
        </select>
        <span style={{ fontSize: 12, color: "#999" }}>{shown.length} of {users.length}</span>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e8e8f0", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 22, cursor: "default" }} aria-label="Open" />
              {cols.map((c) => (
                <th key={c.key} style={{ ...th, textAlign: c.num ? "right" : "left" }} onClick={() => sortBy(c.key)}
                  aria-sort={sort.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                  {c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((u) => {
              const isOpen = open.has(u.id);
              const started = (u.modulesStarted || []).filter((m) => ACCELERATOR_MODULE_LABELS[m]);
              const trial = u.ideationExpiresAt ? accessStatus(u.ideationExpiresAt) : null;
              const cell = {
                name: (
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: "#999", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
                  </div>
                ),
                tier: <span style={TIER_PILL[u.usageTier] || TIER_PILL.free}>{u.credits?.tierLabel || (u.usageTier || "free").replace(/^./, (c) => c.toUpperCase())}</span>,
                access: u.phase
                  ? <span style={{ fontSize: 11, color: "#666" }}>{u.phase}{trial ? <span style={{ color: trial.color }}> · {trial.label}</span> : null}</span>
                  : <span style={{ fontSize: 11, color: "#bbb" }}>none</span>,
                left: !creditsOn || !u.credits ? <span style={{ color: "#bbb" }}>—</span>
                  : <b style={{ color: u.credits.out ? "#b45309" : C.indigo }}>{u.credits.remaining.toLocaleString()}</b>,
                used: u.credits ? u.credits.spent.toLocaleString() : <span style={{ color: "#bbb" }}>—</span>,
                cost: u.totalCost ? `$${u.totalCost.toFixed(2)}` : <span style={{ color: "#bbb" }}>—</span>,
                active: <span style={{ fontSize: 11, color: "#666", whiteSpace: "nowrap" }}>{timeAgo(u.lastActive)}</span>,
              };
              return (
                <Fragment key={u.id}>
                  <tr onClick={() => toggle(u.id)} style={{ cursor: "pointer", background: isOpen ? "#faf9fc" : "#fff" }}>
                    <td style={{ ...td, paddingRight: 0, width: 22 }}>
                      {isOpen ? <ChevronDown size={14} color="#999" /> : <ChevronRight size={14} color="#999" />}
                    </td>
                    {cols.map((c) => (
                      <td key={c.key} style={{ ...td, textAlign: c.num ? "right" : "left", maxWidth: c.key === "name" ? 240 : undefined }}>{cell[c.key]}</td>
                    ))}
                  </tr>
                  {isOpen && (
                    <tr style={{ background: "#faf9fc" }}>
                      <td />
                      <td colSpan={cols.length} style={{ ...td, paddingTop: 2, fontSize: 11.5, color: "#555" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 18px", marginBottom: 8 }}>
                          {isMobile && <span>Access: <b>{u.phase || "none"}</b>{trial ? ` · ${trial.label}` : ""}</span>}
                          {isMobile && u.credits && <span>Credits used: <b>{u.credits.spent.toLocaleString()}</b></span>}
                          {u.totalTokens > 0 && <span>Tokens: <b>{fmtTok(u.totalTokens)}</b>{u.totalCost != null ? <> · <b>${u.totalCost.toFixed(2)}</b></> : null}</span>}
                          {u.archetype && <span style={pill(`${C.teal}14`, C.teal)}>{u.archetype}</span>}
                          <span>Joined {timeAgo(u.createdAt)}</span>
                        </div>
                        <p style={{ margin: "0 0 10px", color: started.length ? "#666" : "#bbb" }}>
                          {started.length
                            ? <><b>{started.length}</b> module{started.length !== 1 ? "s" : ""} started — {started.map((m) => ACCELERATOR_MODULE_LABELS[m]).join(", ")}</>
                            : "No modules started yet"}
                        </p>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {u.totalTokens > 0 && (
                            <button onClick={(e) => { e.stopPropagation(); onOpenUsage(u.id); }} style={actionBtn}>See their usage</button>
                          )}
                          {creditsOn && u.phase && (
                            <button onClick={(e) => { e.stopPropagation(); onGiveCredits(u); }} disabled={grantingId === `${u.id}-credits`} style={actionBtn}>
                              {grantingId === `${u.id}-credits` ? "…" : "Give credits"}
                            </button>
                          )}
                          {(u.phase === "ideation" || !u.phase) && [{ label: "1-day trial", days: 1 }, { label: "30-day trial", days: 30 }].map(({ label, days }) => (
                            <button key={days} onClick={(e) => { e.stopPropagation(); onGrantAccess(u.id, days); }}
                              disabled={grantingId === `${u.id}-${days}`} style={actionBtn}>
                              {grantingId === `${u.id}-${days}` ? "…" : label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {shown.length === 0 && (
              <tr><td colSpan={cols.length + 1} style={{ ...td, textAlign: "center", color: "#bbb", padding: "28px 10px" }}>No one matches.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const actionBtn = {
  fontSize: 11, padding: "4px 10px", borderRadius: 6, border: `1px solid ${C.teal}`,
  background: "#fff", color: C.teal, cursor: "pointer", fontWeight: 600,
};
